import React, { useRef, useState } from "react";
import { Card } from "../card";
import StudentAdmissionFormStepOne from "./studentAdmissionFormStepOne";
import StepWrapper, { StepWrapperHandle } from "../Wrapper/StepWrapper";
import StudentAdmissionFormStepTwo from "./StudentAdmissionFormStepTwo";
import { StudentAdmissionFormStepThree } from "./StudentAdmissionFormStepThree";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import {
  setErrorMessage,
  setSuccessMessage,
  setWarningMessage,
} from "../../Redux/Slices/alert.slice";
import StudentAdmissionFormStepFour from "./StudentAdmissionFormStepFour";
import { useRegisterStudent } from "../../hooks/students/useRegisterStudent.hooks";
import { useCheckStudentExists } from "../../hooks/students/useCheckStudentExists.hooks";
import { ParentsInfo, reset } from "../../Redux/Slices/studentAdmission.slice";
import { SOMETHING_WENT_WRONG } from "../../Constants/messages";
import {
  BRANCH_ID_REQUIRED,
  CLASS_ID_REQUIRED,
  DOB_REQUIRED,
  FIRST_NAME_REQUIRED,
  GENDER_REQUIRED,
  LAST_NAME_REQUIRED,
} from "../../Constants/Message/Student.messages";
import { StudentAcademicFeeForm } from "./StudentAcademicFee";
import { useNavigate } from "react-router-dom";
import { Paths } from "../../Constants/paths";
import queryClient from "../../config/queryClient";
import { STUDENTS_LIST_KEY } from "../../hooks/students/useStudentsLists.hooks";
import { StudentExistsModal } from "./StudentExistsModal";

const Student = () => {
  const {
    selectedParents,
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    fatherInfo,
    motherInfo,
    guardianInfo,
    classId,
    schoolBranchId,
    gender,
    nationality,
    religion,
    address,
    transportId,
    admissionFee,
    annualFee,
    extraClassFee,
    monthlyFee,
    stationeryFee,
    transportationFee,
    examFee,
    extraCirculumFee,
    lunchPackageId,
    registeredAt,
  } = useSelector((state: RootState) => ({
    selectedParents: state.studentAdmission.selectedParents,
    firstName: state.studentAdmission.firstName,
    middleName: state.studentAdmission.middleName,
    lastName: state.studentAdmission.lastName,
    dateOfBirth: state.studentAdmission.dateOfBirth,
    motherInfo: state.studentAdmission.motherInfo,
    fatherInfo: state.studentAdmission.fatherInfo,
    guardianInfo: state.studentAdmission.guardianInfo,
    classId: state.studentAdmission.classId,
    schoolBranchId: state.studentAdmission.schoolBranchId,
    gender: state.studentAdmission.gender,
    nationality: state.studentAdmission.nationality,
    religion: state.studentAdmission.religion,
    address: state.studentAdmission.address,
    transportId: state.studentAdmission.transportId,
    admissionFee: state.studentAdmission.schoolFees.admissionFee,
    annualFee: state.studentAdmission.schoolFees.annualFee,
    extraClassFee: state.studentAdmission.schoolFees.extraClassFee,
    monthlyFee: state.studentAdmission.schoolFees.monthlyFee,
    stationeryFee: state.studentAdmission.schoolFees.stationeryFee,
    transportationFee: state.studentAdmission.schoolFees.transportationFee,
    examFee: state.studentAdmission.schoolFees.examFee,
    extraCirculumFee: state.studentAdmission.schoolFees.extraCirculumFee,
    lunchPackageId: state.studentAdmission.lunchPackagedId,
    registeredAt: state.studentAdmission.registeredAt,
  }));

  const navigate = useNavigate();
  const stepWrapperRef = useRef<StepWrapperHandle>(null);

  const { isLoading: isRegisterginStudent, mutate: registerStudent } =
    useRegisterStudent();

  const { mutateAsync: checkStudentExists } = useCheckStudentExists();

  const [showStudentExistsModal, setShowStudentExistsModal] =
    useState(false);
  const [existingStudentId, setExistingStudentId] = useState<number | null>(
    null,
  );

  const dispatch = useDispatch();

  const validateStepOne = () => {
    if (!selectedParents) {
      dispatch(
        setWarningMessage("Please select the parents before you proceed."),
      );
      return false;
    }
    return true;
  };

  const validateStepTwo = () => {
    if (!classId) {
      dispatch(setWarningMessage(CLASS_ID_REQUIRED));
      return false;
    }
    if (!schoolBranchId) {
      dispatch(setWarningMessage(BRANCH_ID_REQUIRED));
      return false;
    }
    if (!firstName) {
      dispatch(setWarningMessage(FIRST_NAME_REQUIRED));
      return false;
    }
    if (!lastName) {
      dispatch(setWarningMessage(LAST_NAME_REQUIRED));
      return false;
    }
    if (!gender) {
      dispatch(setWarningMessage(GENDER_REQUIRED));
      return false;
    }
    if (!dateOfBirth) {
      dispatch(setWarningMessage(DOB_REQUIRED));
      return false;
    }

    return true;
  };

  // Runs the field checks for step 2, then (only once those pass) asks the
  // backend whether a student with this name/class/branch already exists.
  // If one does, advancing is blocked and a modal is shown instead.
  const handleStepTwoNext = async () => {
    if (!validateStepTwo()) return false;

    try {
      const { studentExists, studentId } = await checkStudentExists({
        studentFirstName: firstName,
        studentLastName: lastName,
        studentMiddleName: middleName,
        branchId: schoolBranchId as number,
        classId: classId as number,
      });

      if (studentExists) {
        setExistingStudentId(studentId);
        setShowStudentExistsModal(true);
        return false;
      }

      return true;
    } catch (error: any) {
      dispatch(setErrorMessage(error?.message || SOMETHING_WENT_WRONG));
      return false;
    }
  };

  const validateStepThree = () => {
    if (!fatherInfo?.name) {
      dispatch(setWarningMessage("Please enter the Father's name."));
      return false;
    }
    if (!fatherInfo.mobile) {
      dispatch(setWarningMessage("Please enter the Father's mobile number."));
      return false;
    }
    if (!motherInfo?.name) {
      dispatch(setWarningMessage("Please enter the Mother's name."));
      return false;
    }
    if (!motherInfo.mobile) {
      dispatch(setWarningMessage("Please enter the Mother's mobile number."));
      return false;
    }
    return true;
  };

  const submitAdmissionForm = () => {
    registerStudent(
      {
        parentId: selectedParents?.id as number,
        branchId: schoolBranchId as number,
        classId: classId as number,
        firstName,
        middleName,
        lastName,
        dateOfBirth,
        motherInfo: motherInfo as ParentsInfo,
        fatherInfo: fatherInfo as ParentsInfo,
        nationality,
        religion,
        guardianInfo,
        gender,
        address,
        transportId: transportId as number,
        schoolFees: {
          admissionFee: admissionFee as number,
          annualFee: annualFee as number,
          extraClassFee: extraClassFee as number,
          monthlyFee: monthlyFee as number,
          stationeryFee: stationeryFee as number,
          transportationFee: transportationFee as number,
          examFee: examFee as number,
          extraCirculumFee: extraCirculumFee as number,
          lunchPackageId: lunchPackageId as number,
        },
        registeredAt,
      },
      {
        onSuccess: () => {
          dispatch(
            setSuccessMessage("Students has been registered successfully"),
          );

          dispatch(reset());

          queryClient.invalidateQueries({
            queryKey: [STUDENTS_LIST_KEY.STUDENTS_LIST],
          });

          navigate(`${Paths.StudentList}`);
        },
        onError: (error: any) => {
          dispatch(
            setErrorMessage(
              error.response.data.message || SOMETHING_WENT_WRONG,
            ),
          );
        },
      },
    );
  };

  const handleStepChange = (step: number) => {
    switch (step) {
      case 1:
        return validateStepOne();
      case 2:
        return handleStepTwoNext();
      case 3:
        return true;
      case 4:
        return validateStepThree();
    }
  };

  return (
    <Card cardTitle="Students admission form">
      <StepWrapper
        ref={stepWrapperRef}
        onStepChange={handleStepChange}
        stepComponents={[
          <StudentAdmissionFormStepOne />,
          <StudentAdmissionFormStepTwo />,
          <StudentAcademicFeeForm />,
          <StudentAdmissionFormStepThree />,
          <StudentAdmissionFormStepFour />,
        ]}
        stepTitles={[
          "Basic Information",
          "Guardian Details",
          "Academic Fees",
          "Transport & Lunch",
          "Review & Submit",
        ]}
        stepDescriptions={[
          "Enter student's personal details",
          "Add parent or guardian information",
          "Configure fee structure and payment",
          "Select transport and meal options",
          "Verify all information before submitting",
        ]}
        onSubmit={() => {
          submitAdmissionForm();
        }}
        submitText="Register"
        isSubmitLoading={isRegisterginStudent}
      />

      <StudentExistsModal
        open={showStudentExistsModal}
        onClose={() => setShowStudentExistsModal(false)}
        onProceedAnyway={() => {
          setShowStudentExistsModal(false);
          stepWrapperRef.current?.goToNextStep();
        }}
        studentName={[firstName, middleName, lastName]
          .filter(Boolean)
          .join(" ")}
        existingStudentId={existingStudentId}
      />
    </Card>
  );
};

export default Student;
